/**
 * Estado del servidor.
 *
 * Yomou depende de un VPS con seis servicios, un certificado que se renueva
 * solo y un descargador que puede atascarse. Todo eso falla callado: el atasco
 * que motivo esta pantalla llevaba dias parando la auto-descarga sin que
 * hubiera forma de enterarse salvo entrando por SSH a mirar.
 *
 * La revision la hace el servidor cada cuarto de hora —y arregla lo que sabe
 * arreglar—; aqui solo se pinta la ultima foto que dejo guardada.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { obtenerSalud, revisarSalud, type EstadoSalud, type Salud as Informe } from '../api/avisos'

const RESUMEN: Record<EstadoSalud, string> = {
  bien: 'Todo en orden',
  ojo: 'Hay algo que mirar',
  mal: 'Algo no esta funcionando',
}

export default function Salud() {
  const clienteConsultas = useQueryClient()

  const { data: salud, isLoading, error } = useQuery({
    queryKey: ['salud'],
    queryFn: obtenerSalud,
    retry: false,
  })

  // La revision a mano sale a probar las fuentes una por una, asi que tarda.
  // El resultado se mete en la cache para que el punto de la pestana cambie a
  // la vez que el panel.
  const revision = useMutation({
    mutationFn: revisarSalud,
    onSuccess: (nueva: Informe) => clienteConsultas.setQueryData(['salud'], nueva),
  })

  return (
    <div className="aviso">
      <strong>Estado del servidor</strong>

      {isLoading && <p className="meta">Comprobando…</p>}
      {error && <p className="meta">No se pudo consultar: {(error as Error).message}</p>}

      {salud && (
        <>
          <p className="salud-resumen">
            <span className={`punto-salud ${salud.estado}`} />
            {RESUMEN[salud.estado]}
            <span className="meta">
              {salud.generado
                ? `Revisado el ${new Date(salud.generado * 1000).toLocaleString('es-ES')}`
                : 'Todavia sin revisar'}
            </span>
          </p>

          <ul className="lista-salud">
            {salud.puntos.map((punto) => (
              <li key={punto.clave}>
                <span className={`punto-salud ${punto.estado}`} />
                <span>
                  <b>{punto.titulo}</b>
                  <span className="meta">{punto.detalle}</span>
                </span>
              </li>
            ))}
          </ul>

          {salud.reparaciones.length > 0 && (
            <div className="salud-reparado">
              <b>Arreglado solo</b>
              {salud.reparaciones.map((texto) => (
                <span className="meta" key={texto}>
                  {texto}
                </span>
              ))}
            </div>
          )}
        </>
      )}

      {revision.error && (
        <p className="error-texto">No se pudo revisar: {(revision.error as Error).message}</p>
      )}

      <button
        className="boton secundario"
        disabled={revision.isPending}
        onClick={() => revision.mutate()}
      >
        {revision.isPending ? 'Revisando…' : 'Revisar ahora'}
      </button>

      <p className="meta">
        El servidor se revisa solo cada 15 minutos, desatasca las descargas que
        puede y te avisa si algo se rompe. Revisar a mano prueba ademas cada
        fuente, y por eso tarda unos segundos.
      </p>
    </div>
  )
}
